import React, { ReactNode } from 'react';
import { cn } from '../../../utils/classNames';
import { Button } from '../../ui';

interface BaseFormContainerProps {
  title: string;
  icon?: ReactNode;
  children: ReactNode;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isLoading?: boolean;
  submitLabel?: string;
  cancelLabel?: string;
  isEdit?: boolean;
  error?: string | null;
  className?: string;
  disableSubmit?: boolean;
}

/**
 * 統一フォームコンテナコンポーネント
 *
 * デザインシステムに準拠した統一されたフォームレイアウトを提供し、
 * エラーハンドリングとローディング状態管理を統一します。
 */
export const BaseFormContainer: React.FC<BaseFormContainerProps> = ({
  title,
  icon,
  children,
  onSubmit,
  onCancel,
  isLoading = false,
  submitLabel,
  cancelLabel = 'キャンセル',
  isEdit = false,
  error = null,
  className,
  disableSubmit = false,
}) => {
  const defaultSubmitLabel = isEdit ? '更新' : '登録';

  return (
    <div className={cn('bg-white dark:bg-gray-800 p-4 sm:p-8 rounded-lg shadow-md', className)}>
      <h2 className='text-lg sm:text-2xl font-bold text-gray-800 dark:text-white mb-4 sm:mb-6 flex items-center'>
        {icon && <span className='h-6 w-6 sm:h-8 sm:w-8 mr-3'>{icon}</span>}
        {title}
      </h2>

      {error && (
        <div className='mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg'>
          <p className='text-red-800 dark:text-red-200 text-sm'>{error}</p>
        </div>
      )}

      <form onSubmit={onSubmit} className='space-y-4 sm:space-y-6'>
        {children}

        <div className='flex flex-col sm:flex-row gap-3 pt-4 sm:pt-6'>
          <Button
            type='submit'
            variant='primary'
            size='lg'
            loading={isLoading}
            className={`flex-1 sm:flex-initial ${disableSubmit ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={isLoading || disableSubmit}
          >
            {submitLabel || defaultSubmitLabel}
          </Button>
          <Button
            type='button'
            variant='secondary'
            size='lg'
            onClick={onCancel}
            disabled={isLoading}
            className='flex-1 sm:flex-initial'
          >
            {cancelLabel}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BaseFormContainer;
